import { load } from '../../../../services/API';
import { HTTPError } from '../../../../support/errors';

import {
  ReasonCode,
  RunStatusOrUnknown,
  toReasonCode,
  toRunStatus,
} from './runHistoryStatus';

export const PAGE_SIZES = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = 25;

export const SORT_KEYS = [
  'runTime',
  'status',
  'sourceModel',
  'sourceModelInstance',
  'scenarioTag',
  'subscriptionStatus',
  'processingMs',
  'queueMs',
  'totalMs',
] as const;
// Target columns exist only in the client-wide view.
export const CLIENT_SORT_KEYS = [
  ...SORT_KEYS,
  'targetModel',
  'targetModelInstance',
] as const;
export type RunHistorySortKey = (typeof CLIENT_SORT_KEYS)[number];

export const FILTER_KEYS = [
  'sourceModel',
  'sourceModelInstance',
  'scenarioTag',
  'subscriptionStatus',
  'status',
  'runTimeFrom',
  'runTimeTo',
  'detail',
] as const;
export const CLIENT_FILTER_KEYS = [
  ...FILTER_KEYS,
  'targetModel',
  'targetModelInstance',
] as const;

export const FILTER_VALUE_COLUMNS = [
  'sourceModel',
  'sourceModelInstance',
] as const;
export const CLIENT_FILTER_VALUE_COLUMNS = [
  ...FILTER_VALUE_COLUMNS,
  'targetModel',
  'targetModelInstance',
] as const;
export type RunHistoryFilterValueColumn =
  (typeof CLIENT_FILTER_VALUE_COLUMNS)[number];

// The allowlist lives in runHistoryStatus.ts, which has no API import.
export { REASON_CODES, toReasonCode } from './runHistoryStatus';
export type { ReasonCode } from './runHistoryStatus';

export type RunHistoryScope =
  | { kind: 'instance'; modelInstanceId: number }
  | { kind: 'client'; clientId: number };

const MAX_FILTER_LENGTH = 200;
export const GENERIC_DETAIL = 'Details are not available.';
export const RUN_HISTORY_ERROR_MESSAGE = 'Run history could not be loaded.';
export const TIMING_SORT_TOO_MANY_ROWS_MESSAGE =
  'Too many runs to sort by time. Narrow the filters or date range.';
export const RUN_DETAIL_ERROR_MESSAGE = 'Run detail could not be loaded.';

export interface IRunHistoryQuery {
  page: number;
  pageSize: number;
  sort?: RunHistorySortKey;
  sortDir?: 'asc' | 'desc';
  filters?: Partial<Record<string, string>>;
}

export interface IRunHistoryRow {
  rowId: string;
  sourceModel: string | null;
  sourceModelInstance: string | null;
  sourceRestricted: boolean;
  targetModel: string;
  targetModelInstance: string;
  reasonCode: ReasonCode | null;
  scenarioTag: string;
  subscriptionStatus: 'Active' | 'Inactive' | 'Unknown';
  runTime: string | null;
  queueMs: number | null;
  processingMs: number | null;
  totalMs: number | null;
  status: RunStatusOrUnknown;
  noNewData: boolean;
  detail: string;
}

export interface IRunHistoryPage {
  items: IRunHistoryRow[];
  page: number;
  pageSize: number;
  total: number;
}

export interface IRunDetail {
  rowId: string;
  kind: 'submission' | 'triggerLog';
  submissionId: number | null;
  triggerLogId: number | null;
  destinationSubmissionId: number | null;
  statusMessage: string | null;
  reasonCode: string | null;
  reasonDetail: string | null;
  truncated: boolean;
  redacted: boolean;
}

// Only two fixed messages exist: no existence oracle, no body echoed.
export class RunHistoryError extends Error {
  constructor(
    message:
      | typeof RUN_HISTORY_ERROR_MESSAGE
      | typeof TIMING_SORT_TOO_MANY_ROWS_MESSAGE = RUN_HISTORY_ERROR_MESSAGE
  ) {
    super(message);
    this.name = 'RunHistoryError';
  }
}

export class RunDetailError extends Error {
  constructor() {
    super(RUN_DETAIL_ERROR_MESSAGE);
    this.name = 'RunDetailError';
  }
}

const isPositiveId = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value > 0;

const isClientScope = (scope: number | RunHistoryScope) =>
  typeof scope === 'object' && scope !== null && scope.kind === 'client';

// A bare number means instance scope (the original call shape).
const basePath = (scope: number | RunHistoryScope) => {
  const resolved: RunHistoryScope =
    typeof scope === 'object' && scope !== null
      ? scope
      : { kind: 'instance', modelInstanceId: scope };
  if (resolved.kind === 'client') {
    if (!isPositiveId(resolved.clientId)) throw new Error('Invalid client id');
    return `/clients/${resolved.clientId}/imdl_run_history`;
  }
  if (!isPositiveId(resolved.modelInstanceId)) {
    throw new Error('Invalid model instance id');
  }
  return `/instances/${resolved.modelInstanceId}/imdl_run_history`;
};

// The API rejects control characters with a 400, so drop them up front.
const stripControlChars = (value: unknown) =>
  typeof value === 'string'
    ? Array.from(value)
        .filter((c) => c.charCodeAt(0) >= 32 && c.charCodeAt(0) !== 127)
        .join('')
    : '';

export const buildRunHistoryUrl = (
  scope: number | RunHistoryScope,
  query: IRunHistoryQuery
): string => {
  const { page, pageSize, sort, sortDir, filters } = query;
  const client = isClientScope(scope);
  const sortKeys: readonly string[] = client ? CLIENT_SORT_KEYS : SORT_KEYS;
  if (!Number.isSafeInteger(page) || page < 1) {
    throw new Error('Invalid page');
  }
  if (!(PAGE_SIZES as readonly number[]).includes(pageSize)) {
    throw new Error('Invalid page size');
  }
  if (sort !== undefined && !sortKeys.includes(sort)) {
    throw new Error('Invalid sort');
  }
  if (sortDir !== undefined && sortDir !== 'asc' && sortDir !== 'desc') {
    throw new Error('Invalid sort direction');
  }

  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (sort) params.set('sort', sort);
  if (sortDir) params.set('sortDir', sortDir);
  // Target filters are dropped in instance scope, like any unknown key.
  (client ? CLIENT_FILTER_KEYS : FILTER_KEYS).forEach((key) => {
    const value = stripControlChars(filters?.[key]);
    if (value !== '') params.set(key, value.slice(0, MAX_FILTER_LENGTH));
  });

  return `${basePath(scope)}?${params.toString()}`;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toStringOrNull = (value: unknown) =>
  typeof value === 'string' ? value : null;

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;

const toCountOrNull = (value: unknown) => (isCount(value) ? value : null);

const toRow = (
  raw: unknown,
  index: number,
  client: boolean
): IRunHistoryRow => {
  const row = isRecord(raw) ? raw : {};
  const status = toRunStatus(row.status);
  const restricted = row.sourceRestricted === true;
  const subscriptionStatus =
    row.subscriptionStatus === 'Active' || row.subscriptionStatus === 'Inactive'
      ? row.subscriptionStatus
      : 'Unknown';
  const runTime =
    typeof row.runTime === 'string' && !Number.isNaN(Date.parse(row.runTime))
      ? row.runTime
      : null;

  return {
    rowId:
      typeof row.rowId === 'string' && row.rowId !== ''
        ? row.rowId
        : `row-${index}`,
    sourceModel: restricted ? null : toStringOrNull(row.sourceModel),
    sourceModelInstance: restricted
      ? null
      : toStringOrNull(row.sourceModelInstance),
    sourceRestricted: restricted,
    targetModel: client ? (toStringOrNull(row.targetModel) ?? '') : '',
    targetModelInstance: client
      ? (toStringOrNull(row.targetModelInstance) ?? '')
      : '',
    reasonCode: client ? toReasonCode(row.reasonCode) : null,
    scenarioTag: toStringOrNull(row.subscriptionScenarioTag) ?? '',
    subscriptionStatus,
    runTime,
    queueMs: toCountOrNull(row.queueMs),
    processingMs: toCountOrNull(row.processingMs),
    totalMs: toCountOrNull(row.totalMs),
    status,
    noNewData: row.noNewData === true,
    // An unrecognised status means the row is not trusted; show no server text.
    detail:
      status !== 'Unknown' && typeof row.detail === 'string'
        ? row.detail
        : GENERIC_DETAIL,
  };
};

export const parseRunHistoryResponse = (
  body: unknown,
  scope: number | RunHistoryScope = 0
): IRunHistoryPage => {
  if (
    !isRecord(body) ||
    !Array.isArray(body.items) ||
    !isCount(body.total) ||
    !isCount(body.page) ||
    !isCount(body.pageSize)
  ) {
    throw new RunHistoryError();
  }
  const client = isClientScope(scope);
  return {
    items: body.items.map((raw, index) => toRow(raw, index, client)),
    page: body.page,
    pageSize: body.pageSize,
    total: body.total,
  };
};

const isAbort = (error: unknown) =>
  error instanceof Error && error.name === 'AbortError';

export const fetchRunHistory = async (
  scope: number | RunHistoryScope,
  query: IRunHistoryQuery,
  signal?: AbortSignal
): Promise<IRunHistoryPage> => {
  const url = buildRunHistoryUrl(scope, query);
  try {
    return parseRunHistoryResponse(await load(url, { signal }), scope);
  } catch (error) {
    if (isAbort(error)) throw error;
    // Exact code match only; the server's own text is never shown.
    if (
      error instanceof HTTPError &&
      error.status === 400 &&
      error.body?.code === 'TIMING_SORT_TOO_MANY_ROWS'
    ) {
      throw new RunHistoryError(TIMING_SORT_TOO_MANY_ROWS_MESSAGE);
    }
    throw new RunHistoryError();
  }
};

export const fetchFilterValues = async (
  scope: number | RunHistoryScope,
  column: RunHistoryFilterValueColumn,
  signal?: AbortSignal
): Promise<string[]> => {
  const columns: readonly string[] = isClientScope(scope)
    ? CLIENT_FILTER_VALUE_COLUMNS
    : FILTER_VALUE_COLUMNS;
  if (!columns.includes(column)) {
    throw new Error('Invalid filter column');
  }
  const url = `${basePath(scope)}/filter_values?column=${column}`;
  try {
    const body = await load(url, { signal });
    if (!isRecord(body) || !Array.isArray(body.values)) {
      throw new RunHistoryError();
    }
    return body.values.filter((v): v is string => typeof v === 'string');
  } catch (error) {
    if (isAbort(error)) throw error;
    throw new RunHistoryError();
  }
};

const ROW_ID_PATTERN = /^[SL]-[1-9][0-9]{0,9}$/;

const DETAIL_KEYS = [
  'rowId',
  'kind',
  'submissionId',
  'triggerLogId',
  'destinationSubmissionId',
  'statusMessage',
  'reasonCode',
  'reasonDetail',
  'truncated',
  'redacted',
];

const isNullableId = (value: unknown) => value === null || isPositiveId(value);
const isNullableString = (value: unknown) =>
  value === null || typeof value === 'string';

// Exactly the ten keys with the stated types; anything else is untrusted.
const parseRunDetail = (body: unknown): IRunDetail => {
  if (
    !isRecord(body) ||
    Object.keys(body).length !== DETAIL_KEYS.length ||
    !DETAIL_KEYS.every((key) =>
      Object.prototype.hasOwnProperty.call(body, key)
    ) ||
    typeof body.rowId !== 'string' ||
    (body.kind !== 'submission' && body.kind !== 'triggerLog') ||
    !isNullableId(body.submissionId) ||
    !isNullableId(body.triggerLogId) ||
    !isNullableId(body.destinationSubmissionId) ||
    !isNullableString(body.statusMessage) ||
    !isNullableString(body.reasonCode) ||
    !isNullableString(body.reasonDetail) ||
    typeof body.truncated !== 'boolean' ||
    typeof body.redacted !== 'boolean'
  ) {
    throw new RunDetailError();
  }
  return body as unknown as IRunDetail;
};

export const fetchRunDetail = async (
  clientId: number,
  rowId: string,
  signal?: AbortSignal
): Promise<IRunDetail> => {
  if (
    !isPositiveId(clientId) ||
    typeof rowId !== 'string' ||
    !ROW_ID_PATTERN.test(rowId)
  ) {
    throw new RunDetailError();
  }
  const url = `/clients/${clientId}/imdl_run_history/rows/${encodeURIComponent(rowId)}/run_detail`;
  try {
    return parseRunDetail(await load(url, { signal }));
  } catch (error) {
    if (isAbort(error)) throw error;
    throw new RunDetailError();
  }
};
